/**
 * Received customer cheques (v3 "Post-Dated Cheques" layout, BE-21 data)
 *
 * Route: /payments/cheques — also embedded as Wallets → Cheques → Received.
 * A recorded cheque is a pending credit on the customer's wallet until it clears: Clear (deposit bank + date),
 * Bounce (reason, optional bank charge) or Return it to the customer. Search, status, dates and paging run in the database.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Paper, Typography, Button, TextField, MenuItem, Alert, Snackbar, Dialog, DialogTitle, DialogContent,
  DialogActions, Table, TableHead, TableBody, TableRow, TableCell, TableContainer, TablePagination, CircularProgress,
  Grid, IconButton, Tooltip, InputAdornment, Stack, FormControl, InputLabel, Select,
  Autocomplete, useTheme,
} from '@mui/material';
import {
  Add as AddIcon, Refresh as RefreshIcon, Search as SearchIcon, AccountBalance as BankIcon, Warning as WarningIcon
} from '@mui/icons-material';
import { StatusChip } from '../../components/accounting';
import { formatCurrency, formatDate } from '../../utils/accounting/formatters';
import { getBrandedTableHeadSx } from '../../utils/colorUtils';
import { ProTableStats } from '../../components/pro-table';
import { can } from '../../utils/roleUtils';
import useOrganizationSearch from '../../hooks/useOrganizationSearch';
import ApiService from '../../services/api.service';
import { receivedChequeService } from '../../services/accounting/receivedCheque.service';
import { serverMessage, unwrapList } from '../../utils/apiConvert';

const fontSize = { xs: '0.75rem', sm: '0.875rem', md: '1rem' };
const today = () => new Date().toISOString().slice(0, 10);
const twoDecimals = (v) => /^\d+(\.\d{1,2})?$/.test(String(v));
const STATUS_COLOR = { RECEIVED: 'warning', CLEARED: 'success', BOUNCED: 'error', RETURNED: 'default' };
const STATUS_LABEL = { RECEIVED: 'Pending', CLEARED: 'Cleared', BOUNCED: 'Bounced', RETURNED: 'Returned' };
const EMPTY_ADD = { org: null, drawerBank: '', chequeNumber: '', amount: '', chequeDate: today() };

const sessionUser = () => {
  try { return JSON.parse(localStorage.getItem('currentUser') || '{}'); } catch (e) { return {}; }
};

const useDebounced = (value, ms) => {
  const [v, setV] = useState(value);
  useEffect(() => { const t = setTimeout(() => setV(value), ms); return () => clearTimeout(t); }, [value, ms]);
  return v;
};

const ReceivedCheques = ({ bankAccounts: bankAccountsProp, canWrite: canWriteProp, onChanged, embedded = false, viewSwitch = null }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const user = sessionUser();
  const typingCenterId = user.typingCenterId || user.typing_center_id;
  const canWrite = canWriteProp ?? can(user, 'finance', 'write');
  const [rows, setRows] = useState({ content: [], totalElements: 0 });
  const [summary, setSummary] = useState({ totalCount: 0, pendingAmount: 0, clearedAmount: 0, bouncedCount: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState(null);
  const [busy, setBusy] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [banks, setBanks] = useState(bankAccountsProp || []);
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState(EMPTY_ADD);
  const [action, setAction] = useState(null);
  const [form, setForm] = useState({});
  const q = useDebounced(search.trim(), 350);
  const { options: orgs, loading: orgsLoading, onInputChange, filterOptions } = useOrganizationSearch({
    typingCenterId, enabled: addOpen, mapOption: (o) => ({ id: o.id, name: o.name || o.organization_name }),
  });

  useEffect(() => {
    if (bankAccountsProp) { setBanks(bankAccountsProp); return; }
    ApiService.getTcBankAccounts().then((r) => setBanks(unwrapList(r).items)).catch(() => setBanks([]));
  }, [bankAccountsProp]);
  const depositBanks = banks.filter((b) => b.isActive !== false && b.accountType === 'OPERATIONS');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [list, sum] = await Promise.all([
        receivedChequeService.list({
          status: statusFilter === 'ALL' ? undefined : statusFilter, q: q || undefined,
          from: dateFrom || undefined, to: dateTo || undefined, page, size: rowsPerPage,
        }),
        receivedChequeService.summary(),
      ]);
      setRows(list);
      setSummary(sum);
    } catch (err) {
      setError(serverMessage(err, 'Failed to load cheques'));
    } finally {
      setLoading(false);
    }
  }, [statusFilter, q, dateFrom, dateTo, page, rowsPerPage]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { setPage(0); }, [q]);

  const run = async (fn, done) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      setSnackbar(done);
      setAction(null);
      setAddOpen(false);
      await fetchData();
      onChanged?.();
    } catch (err) {
      setError(serverMessage(err, 'The action failed'));
      setAction(null);
    } finally {
      setBusy(false);
    }
  };

  const openAction = (kind, cheque) => {
    setForm({ date: today(), bankAccountId: depositBanks[0]?.id || '', reason: '', bankCharge: '', chargeBankId: '' });
    setAction({ kind, cheque });
  };

  const addValid = addForm.org && addForm.chequeNumber.trim() && addForm.chequeDate && Number(addForm.amount) > 0 && twoDecimals(addForm.amount);
  const actionValid = (() => {
    if (!action) return false;
    if (action.kind === 'clear') return form.date && form.bankAccountId && form.date <= today() && form.date >= action.cheque.chequeDate;
    if (action.kind === 'bounce') {
      const chargeOk = form.bankCharge === '' || (Number(form.bankCharge) > 0 && twoDecimals(form.bankCharge) && form.chargeBankId);
      return form.date && form.date <= today() && form.reason.trim() && chargeOk;
    }
    return form.reason.trim() && form.reason.trim().length <= 255;
  })();

  const confirm = () => {
    const { kind, cheque } = action;
    if (kind === 'clear') {
      run(() => receivedChequeService.clear(cheque.id, form.date, form.bankAccountId), 'Cheque cleared — wallet credited');
    } else if (kind === 'bounce') {
      run(() => receivedChequeService.bounce(cheque.id, {
        date: form.date,
        reason: form.reason.trim(),
        bankCharge: form.bankCharge === '' ? undefined : form.bankCharge,
        bankAccountId: form.bankCharge === '' ? undefined : form.chargeBankId,
      }), 'Cheque marked as bounced — the pending credit is removed');
    } else {
      run(() => receivedChequeService.returnToCustomer(cheque.id, form.reason.trim()), 'Cheque returned to the customer');
    }
  };

  const isDue = (c) => c.status === 'RECEIVED' && c.chequeDate <= today();

  const renderActions = (c) => {
    if (c.status !== 'RECEIVED' || !canWrite) return <Typography sx={{ fontSize: fontSize.xs }}>-</Typography>;
    const early = c.chequeDate > today();
    return (
      <Box display="flex" gap={0.5} justifyContent="center">
        <Tooltip title={early ? `Can be cleared from ${formatDate(c.chequeDate)}` : 'Mark Cleared'}>
          <span>
            <Button size="small" variant="outlined" color="success" disabled={early} onClick={() => openAction('clear', c)}
              sx={{ fontSize: fontSize.xs, minWidth: 60 }}>Cleared</Button>
          </span>
        </Tooltip>
        <Tooltip title="Mark Bounced">
          <Button size="small" variant="outlined" color="error" onClick={() => openAction('bounce', c)} sx={{ fontSize: fontSize.xs, minWidth: 60 }}>Bounced</Button>
        </Tooltip>
        <Tooltip title="Return to customer">
          <Button size="small" variant="outlined" color="inherit" onClick={() => openAction('return', c)} sx={{ fontSize: fontSize.xs, minWidth: 60 }}>Return</Button>
        </Tooltip>
      </Box>
    );
  };


  return (
    <Box sx={{ width: 0, minWidth: '100%' }}>
      {!embedded && (
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Box>
            <Typography variant="h5" fontWeight={600} color="secondary.main">Post-Dated Cheques</Typography>
            <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary' }}>Manage customer cheques, clearances and bounces</Typography>
          </Box>
        </Box>
      )}

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {/* Filters */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mb: 1.5 }}>
        {viewSwitch}
        <TextField size="small" placeholder="Search cheque # or customer..." value={search} onChange={(e) => setSearch(e.target.value)}
          sx={{ flex: '1 1 200px', minWidth: 180, ...{ '& .MuiInputBase-root': { height: 32 }, '& .MuiInputBase-input, & .MuiSelect-select': { fontSize: '0.8125rem' }, '& .MuiInputLabel-root': { fontSize: '0.8125rem' } } }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: 'text.disabled' }} /></InputAdornment> }} />
        <FormControl size="small" sx={{ minWidth: 140, ...{ '& .MuiInputBase-root': { height: 32 }, '& .MuiInputBase-input, & .MuiSelect-select': { fontSize: '0.8125rem' }, '& .MuiInputLabel-root': { fontSize: '0.8125rem' } } }}>
          <InputLabel>Status</InputLabel>
          <Select value={statusFilter} label="Status" onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}>
            <MenuItem value="ALL">All Statuses</MenuItem>
            {Object.entries(STATUS_LABEL).map(([k, l]) => <MenuItem key={k} value={k}>{l}</MenuItem>)}
          </Select>
        </FormControl>
        <TextField size="small" label="From" type="date" value={dateFrom} InputLabelProps={{ shrink: true }} sx={{ width: 150, ...{ '& .MuiInputBase-root': { height: 32 }, '& .MuiInputBase-input, & .MuiSelect-select': { fontSize: '0.8125rem' }, '& .MuiInputLabel-root': { fontSize: '0.8125rem' } } }}
          onChange={(e) => { setDateFrom(e.target.value); setPage(0); }} />
        <TextField size="small" label="To" type="date" value={dateTo} InputLabelProps={{ shrink: true }} sx={{ width: 150, ...{ '& .MuiInputBase-root': { height: 32 }, '& .MuiInputBase-input, & .MuiSelect-select': { fontSize: '0.8125rem' }, '& .MuiInputLabel-root': { fontSize: '0.8125rem' } } }}
          onChange={(e) => { setDateTo(e.target.value); setPage(0); }} />
        <Box sx={{ display: 'flex', gap: 0.5, ml: 'auto', flexShrink: 0, alignItems: 'center' }}>
          <Tooltip title="Refresh">
            <IconButton size="small" onClick={fetchData} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}><RefreshIcon fontSize="small" /></IconButton>
          </Tooltip>
          {canWrite && (
            <Button variant="contained" size="small" startIcon={<AddIcon sx={{ fontSize: 16 }} />} sx={{ fontSize: '0.8125rem', textTransform: 'none', whiteSpace: 'nowrap', ml: 0.5 }}
              onClick={() => { setAddForm(EMPTY_ADD); setAddOpen(true); }}>
              Add Cheque
            </Button>
          )}
        </Box>
      </Box>

      {/* Summary */}
      <ProTableStats
        cards={[
          { label: 'Total Cheques', value: summary.totalCount },
          { label: 'Pending Amount', value: formatCurrency(summary.pendingAmount), color: 'warning.main' },
          { label: 'Cleared Amount', value: formatCurrency(summary.clearedAmount), color: 'success.main' },
          { label: 'Bounced', value: summary.bouncedCount, color: summary.bouncedCount > 0 ? 'error.main' : undefined },
        ]}
      />

      {/* Table */}
      <Paper>
        <TableContainer>
          <Table size="small">
            <TableHead sx={getBrandedTableHeadSx(theme)}>
              <TableRow>
                <TableCell sx={{ fontSize: fontSize.xs, fontWeight: 600 }}>Cheque #</TableCell>
                <TableCell sx={{ fontSize: fontSize.xs, fontWeight: 600 }}>Customer</TableCell>
                <TableCell sx={{ fontSize: fontSize.xs, fontWeight: 600 }}>Bank</TableCell>
                <TableCell align="right" sx={{ fontSize: fontSize.xs, fontWeight: 600 }}>Amount</TableCell>
                <TableCell sx={{ fontSize: fontSize.xs, fontWeight: 600 }}>Cheque Date</TableCell>
                <TableCell align="center" sx={{ fontSize: fontSize.xs, fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ fontSize: fontSize.xs, fontWeight: 600 }}>Deposit Date</TableCell>
                <TableCell align="center" sx={{ fontSize: fontSize.xs, fontWeight: 600 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4 }}><CircularProgress size={28} /></TableCell></TableRow>
              ) : rows.content.length === 0 ? (
                <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4 }}><Typography color="text.secondary" sx={{ fontSize: fontSize.sm }}>No cheques found</Typography></TableCell></TableRow>
              ) : rows.content.map((c) => (
                <TableRow key={c.id} hover sx={{ ...(isDue(c) && { bgcolor: isDark ? 'rgba(255,152,0,0.08)' : '#fff8e1' }) }}>
                  <TableCell sx={{ py: 1 }}>
                    <Typography sx={{ fontSize: '13px', fontWeight: 500 }}>{c.chequeNumber}</Typography>
                    <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>Received {formatDate(c.receivedOn)}</Typography>
                  </TableCell>
                  <TableCell sx={{ py: 1 }}><Typography sx={{ fontSize: '13px', fontWeight: 500 }}>{c.customerName || '—'}</Typography></TableCell>
                  <TableCell sx={{ py: 1 }}>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <BankIcon sx={{ fontSize: 14 }} />
                      <Typography sx={{ fontSize: '13px' }}>{c.drawerBank || '—'}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="right" sx={{ py: 1 }}><Typography sx={{ fontSize: '13px', fontWeight: 600 }}>{formatCurrency(c.amount)}</Typography></TableCell>
                  <TableCell sx={{ py: 1 }}>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <Typography sx={{ fontSize: '13px', color: isDue(c) ? '#ff9800' : 'text.primary' }}>{formatDate(c.chequeDate)}</Typography>
                      {isDue(c) && <Tooltip title="Due — can be cleared"><WarningIcon sx={{ fontSize: 14, color: '#ff9800' }} /></Tooltip>}
                    </Box>
                  </TableCell>
                  <TableCell align="center" sx={{ py: 1 }}>
                    <Tooltip title={c.closeReason ? `Reason: ${c.closeReason}` : ''}>
                      <span><StatusChip tone={STATUS_COLOR[c.status]} label={STATUS_LABEL[c.status] || c.status} /></span>
                    </Tooltip>
                  </TableCell>
                  <TableCell sx={{ fontSize: '13px', py: 1 }}>{c.status === 'CLEARED' ? formatDate(c.closedOn) : '—'}</TableCell>
                  <TableCell align="center" sx={{ py: 1 }}>{renderActions(c)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination component="div" count={rows.totalElements} page={page} onPageChange={(e, p) => setPage(p)} rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }} rowsPerPageOptions={[10, 25, 50]} />
      </Paper>

      {/* Add Cheque */}
      <Dialog open={addOpen} onClose={() => setAddOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontSize: fontSize.md, fontWeight: 600 }}>Add Post-Dated Cheque</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary', mt: 0.5 }}>
            The amount shows as a pending credit on the customer&apos;s wallet until the cheque clears.
          </Typography>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <Autocomplete options={orgs} loading={orgsLoading} value={addForm.org} filterOptions={filterOptions}
                getOptionLabel={(o) => o.name || ''} isOptionEqualToValue={(a, b) => a.id === b.id}
                onInputChange={onInputChange} onChange={(e, val) => setAddForm((f) => ({ ...f, org: val }))}
                renderInput={(params) => <TextField {...params} size="small" label="Customer *" />} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Bank" placeholder="Customer's bank (drawer)" value={addForm.drawerBank}
                onChange={(e) => setAddForm((f) => ({ ...f, drawerBank: e.target.value }))} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" label="Cheque Number *" value={addForm.chequeNumber}
                onChange={(e) => setAddForm((f) => ({ ...f, chequeNumber: e.target.value }))} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" type="number" label="Amount *" value={addForm.amount}
                onChange={(e) => setAddForm((f) => ({ ...f, amount: e.target.value }))} error={addForm.amount !== '' && !twoDecimals(addForm.amount)}
                InputProps={{ endAdornment: <InputAdornment position="end">AED</InputAdornment>, inputProps: { min: 0, step: '0.01' } }} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" type="date" label="Cheque Date *" value={addForm.chequeDate} InputLabelProps={{ shrink: true }}
                helperText="May be in the future" onChange={(e) => setAddForm((f) => ({ ...f, chequeDate: e.target.value }))} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddOpen(false)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" disabled={busy || !addValid} sx={{ textTransform: 'none' }}
            onClick={() => run(() => receivedChequeService.receive({
              internalOrganizationId: addForm.org.id, chequeNumber: addForm.chequeNumber.trim(), chequeDate: addForm.chequeDate,
              drawerBank: addForm.drawerBank.trim() || undefined, amount: addForm.amount,
            }), 'Cheque added — pending credit on the wallet')}>
            Add Cheque
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cleared / Bounced / Return */}
      <Dialog open={!!action} onClose={() => setAction(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: fontSize.md, fontWeight: 600 }}>
          {{ clear: 'Mark Cheque as Cleared', bounce: 'Mark Cheque as Bounced', return: 'Return Cheque to Customer' }[action?.kind]}
        </DialogTitle>
        <DialogContent>
          {action && (
            <Box sx={{ bgcolor: isDark ? 'rgba(255,255,255,0.04)' : '#f5f5f5', borderRadius: 1, p: 2, mb: 2 }}>
              <Typography sx={{ fontSize: fontSize.sm, fontWeight: 600 }}>Cheque {action.cheque.chequeNumber} — {formatCurrency(action.cheque.amount)}</Typography>
              <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary' }}>
                {action.cheque.customerName}.{' '}
                {action.kind === 'clear' && 'The wallet is credited and the deposit bank receives the money.'}
                {action.kind === 'bounce' && 'The pending wallet credit is removed.'}
                {action.kind === 'return' && 'The cheque goes back to the customer; the pending wallet credit is removed.'}
              </Typography>
            </Box>
          )}
          <Stack spacing={2}>
            {action?.kind !== 'return' && (
              <TextField fullWidth size="small" type="date" label="Date *" value={form.date || ''} InputLabelProps={{ shrink: true }}
                inputProps={{ max: today() }} onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} />
            )}
            {action?.kind === 'clear' && (
              <TextField select fullWidth size="small" label="Deposited to *" value={form.bankAccountId || ''}
                onChange={(e) => setForm((f) => ({ ...f, bankAccountId: e.target.value }))}>
                {depositBanks.map((b) => <MenuItem key={b.id} value={b.id}>{[b.bankName, b.accountName].filter(Boolean).join(' - ')}</MenuItem>)}
              </TextField>
            )}
            {action?.kind !== 'clear' && (
              <TextField fullWidth size="small" label={action?.kind === 'bounce' ? 'Bounce Reason *' : 'Reason *'} value={form.reason || ''}
                onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))} />
            )}
            {action?.kind === 'bounce' && (
              <>
                <TextField fullWidth size="small" type="number" label="Bank charge (optional)" value={form.bankCharge || ''}
                  onChange={(e) => setForm((f) => ({ ...f, bankCharge: e.target.value }))}
                  InputProps={{ endAdornment: <InputAdornment position="end">AED</InputAdornment>, inputProps: { min: 0, step: '0.01' } }} />
                {form.bankCharge !== '' && (
                  <TextField select fullWidth size="small" label="Charged by bank *" value={form.chargeBankId || ''}
                    onChange={(e) => setForm((f) => ({ ...f, chargeBankId: e.target.value }))}>
                    {depositBanks.map((b) => <MenuItem key={b.id} value={b.id}>{[b.bankName, b.accountName].filter(Boolean).join(' - ')}</MenuItem>)}
                  </TextField>
                )}
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAction(null)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" color={action?.kind === 'bounce' ? 'error' : 'primary'} disabled={busy || !actionValid} onClick={confirm} sx={{ textTransform: 'none' }}>
            {{ clear: 'Confirm Cleared', bounce: 'Confirm Bounced', return: 'Confirm Return' }[action?.kind]}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!snackbar} autoHideDuration={3500} onClose={() => setSnackbar(null)} message={snackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />
    </Box>
  );
};

export default ReceivedCheques;
